/**
 * Error Helpers
 *
 * Utilities for identifying and handling specific API error types.
 */

import {
  limitLabels,
  USAGE_AUTHORITY_UNAVAILABLE_CODE,
  USAGE_AUTHORITY_UNAVAILABLE_MESSAGE,
} from '@beheard/limits';

/**
 * An RTK Query error carrying the API's envelope (backend ApiResponse.error):
 *
 *   { success: false, message, error: { code, details } }
 *
 * The stable code is at `data.error.code` — never `data.code`, which the API
 * does not send. `data.code` is read only as a fallback for older bodies.
 */
export interface ApiError {
  status?: number;
  data?: {
    message?: string;
    error?: { code?: string; details?: unknown } | null;
    /** Legacy bodies only. */
    code?: string;
    [key: string]: unknown;
  };
}

/** The server's stable error code (`error.code` of the envelope), or null. */
export function apiErrorCode(error: unknown): string | null {
  const data = (error as ApiError | null)?.data;
  if (!data || typeof data !== 'object') return null;
  const code = data.error?.code ?? data.code;
  return typeof code === 'string' && code ? code : null;
}

/** The envelope's `error.details` object, or null. */
export function apiErrorDetails(error: unknown): Record<string, unknown> | null {
  const details = (error as ApiError | null)?.data?.error?.details;
  return details && typeof details === 'object' ? (details as Record<string, unknown>) : null;
}

/**
 * Check if error is a plan limit error (user exceeded their plan's limits).
 */
export function isPlanLimitError(error: unknown): error is ApiError {
  return (error as ApiError)?.status === 403 && apiErrorCode(error) === 'PLAN_LIMIT';
}

/**
 * Check if error is an org plan limit error (org exceeded their plan's limits).
 */
export function isOrgPlanLimitError(error: unknown): error is ApiError {
  return (error as ApiError)?.status === 403 && apiErrorCode(error) === 'ORG_PLAN_LIMIT';
}

/**
 * Check if error is a usage/credits blocked error.
 */
export function isUsageBlockedError(error: unknown): error is ApiError {
  const status = (error as ApiError)?.status;
  return (status === 402 || status === 403) && apiErrorCode(error) === 'USAGE_BLOCKED';
}

/**
 * Check if error is any type of upgrade-required error.
 */
export function isUpgradeRequiredError(error: unknown): boolean {
  return isPlanLimitError(error) || isOrgPlanLimitError(error) || isUsageBlockedError(error);
}

/**
 * Get a user-friendly error message from an API error.
 */
export function getErrorMessage(error: unknown, defaultMessage = 'An error occurred'): string {
  const message = (error as ApiError)?.data?.message;
  return typeof message === 'string' && message ? message : defaultMessage;
}

function detailString(error: unknown, key: string): string | undefined {
  const v = apiErrorDetails(error)?.[key] ?? (error as ApiError)?.data?.[key];
  return typeof v === 'string' && v ? v : undefined;
}

/**
 * Get the limit key from a plan limit error.
 */
export function getLimitKey(error: unknown): string | undefined {
  if (isPlanLimitError(error) || isOrgPlanLimitError(error)) {
    return detailString(error, 'limitKey');
  }
  return undefined;
}

/**
 * Get plan key from a plan limit error.
 */
export function getPlanKey(error: unknown): string | undefined {
  if (isPlanLimitError(error)) {
    return detailString(error, 'planKey');
  }
  return undefined;
}

/**
 * Get org plan key from an org plan limit error.
 */
export function getOrgPlanKey(error: unknown): string | undefined {
  if (isOrgPlanLimitError(error)) {
    return detailString(error, 'orgPlanKey');
  }
  return undefined;
}

/**
 * Human-readable limit key labels — from the one limit copy the server words
 * its refusals with (`@beheard/limits`), user and organization keys alike.
 */
export const LIMIT_KEY_LABELS: Record<string, string> = limitLabels();

/**
 * Get a human-readable label for a limit key.
 */
export function getLimitLabel(limitKey: string): string {
  return LIMIT_KEY_LABELS[limitKey] || limitKey.replace(/([A-Z])/g, ' $1').toLowerCase().trim();
}

/**
 * A 403 PLAN_LIMIT refusal, read from the envelope the API actually sends
 * (ApiResponse.error):
 *
 *   { success: false, message, error: { code: 'PLAN_LIMIT',
 *     details: { limitKey, planKey, currentValue, maxValue, ... } } }
 *
 * (isPlanLimitError above reads `data.code`, which that envelope never has.)
 * The message is the server's own, shown as sent. Null for anything else.
 */
export interface PlanLimitNotice {
  message: string;
  limitKey?: string;
  planKey?: string;
  currentValue?: number;
  maxValue?: number;
}

export function planLimitNotice(error: unknown): PlanLimitNotice | null {
  const err = error as {
    status?: unknown;
    data?: {
      message?: unknown;
      code?: unknown;
      error?: { code?: unknown; details?: unknown } | null;
    } | null;
  } | null;
  if (err?.status !== 403) return null;
  if (apiErrorCode(error) !== 'PLAN_LIMIT') return null;
  const raw = err.data?.error?.details;
  const details = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
  return {
    message:
      str(err.data?.message) ?? str(details.message) ?? "You've reached your plan's limit. Upgrade to continue.",
    limitKey: str(details.limitKey),
    planKey: str(details.planKey),
    currentValue: num(details.currentValue),
    maxValue: num(details.maxValue),
  };
}

/**
 * A 503 USAGE_AUTHORITY_UNAVAILABLE: the server could not check usage, so it
 * did nothing and counted nothing. Temporary — NOT a limit, so it never opens
 * the upgrade prompt. Returns the message to show (the server's, or the same
 * single-sourced copy), or null for any other error.
 */
export function usageAuthorityUnavailableMessage(error: unknown): string | null {
  const err = error as {
    status?: unknown;
    data?: { message?: unknown; code?: unknown; error?: { code?: unknown } | null } | null;
  } | null;
  if (err?.status !== 503) return null;
  if (apiErrorCode(error) !== USAGE_AUTHORITY_UNAVAILABLE_CODE) return null;
  const message = err.data?.message;
  return typeof message === 'string' && message ? message : USAGE_AUTHORITY_UNAVAILABLE_MESSAGE;
}
